import { Button, Grid, Message, Modal } from "@intility/bifrost-react";
import { useDeleteClient } from "../api/clients";
import type { ClientListItem } from "../api/types";

// Confirms deleting a client, the same wording as the client page. The client is
// gone from the portal's lists once this succeeds (the delete hook refetches
// them); `onDeleted` lets the host react, e.g. by leaving the client's page.
export function ClientDeleteModal({
  client,
  open,
  onClose,
  onDeleted,
}: {
  client: ClientListItem;
  open: boolean;
  onClose: () => void;
  onDeleted?: () => void;
}) {
  const deleteClient = useDeleteClient();

  function close() {
    if (deleteClient.isPending) return;
    onClose();
    deleteClient.reset();
  }

  async function confirm() {
    try {
      await deleteClient.mutateAsync(client.id);
      onClose();
      onDeleted?.();
    } catch {
      // The error shows from the mutation state.
    }
  }

  return (
    <Modal
      isOpen={open}
      onRequestClose={close}
      header="Delete client"
      width={480}
    >
      <Grid gap={16}>
        <p className="bf-p" style={{ margin: 0 }}>
          This permanently deletes <strong>{client.name}</strong> and revokes
          all its tokens. This cannot be undone.
        </p>
        {deleteClient.error && (
          <Message state="alert" header="Delete failed">
            {deleteClient.error instanceof Error
              ? deleteClient.error.message
              : "Something went wrong."}
          </Message>
        )}
        <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
          <Button
            variant="flat"
            onClick={close}
            state={deleteClient.isPending ? "inactive" : undefined}
          >
            Cancel
          </Button>
          <Button
            variant="filled"
            state={deleteClient.isPending ? "inactive" : "alert"}
            onClick={deleteClient.isPending ? undefined : confirm}
          >
            {deleteClient.isPending ? "Deleting…" : "Delete client"}
          </Button>
        </div>
      </Grid>
    </Modal>
  );
}
